import React, { useEffect, useState, useCallback } from "react";
import { parseISO, format } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Drawer from "@material-ui/core/Drawer";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import CircularProgress from "@material-ui/core/CircularProgress";
import SvgIcon from "@material-ui/core/SvgIcon";
import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import api from "../../services/api";

const getDocIconSrc = (ext) => {
	const lower = (ext || "").toLowerCase();
	if (lower === "pdf") return "/imagens/PDF.png";
	if (["doc", "docx"].includes(lower)) return "/imagens/word.png";
	if (["xls", "xlsx", "csv"].includes(lower)) return "/imagens/excel.png";
	if (lower === "txt") return "/imagens/txt.png";
	return "/imagens/doc.png";
};

const DocFileIcon = ({ ext, size = 40 }) => (
	<img
		src={getDocIconSrc(ext)}
		alt={ext ? ext.toUpperCase() : "DOC"}
		style={{ width: size, height: size, objectFit: "contain" }}
	/>
);

const CloseXIcon = (props) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</SvgIcon>
);

const useStyles = makeStyles(theme => ({
	drawer: {
		"& .MuiDrawer-paper": {
			...theme.chatiaDS.dialogPaper,
			width: 420,
			maxWidth: "85vw",
			maxHeight: "none !important",
			height: "100%",
			backgroundColor: theme.mode === "light" ? "#ffffff !important" : "#121212 !important",
			backgroundImage: "none !important",
			borderRadius: "21px 0 0 12px !important",
			border: "none",
			boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
		},
	},
	header: {
		...theme.chatiaDS.header,
	},
	headerTitle: {
		...theme.chatiaDS.headerTitle,
	},
	closeBtn: {
		backgroundColor: theme.chatiaDS.closeButton.backgroundColor,
		color: theme.chatiaDS.closeButton.color,
		transition: theme.chatiaDS.closeButton.transition,
	},
	tabs: {
		minHeight: 36,
		borderBottom: theme.mode === "light" ? "1px solid #e8e8e8" : "1px solid #2a3942",
		"& .MuiTab-root": {
			minHeight: 36,
			textTransform: "none",
			fontSize: 13,
			fontWeight: 500,
			minWidth: 0,
			flex: 1,
		},
		"& .MuiTabs-indicator": {
			height: 2,
		},
	},
	content: {
		flex: 1,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	monthLabel: {
		fontSize: 13,
		fontWeight: 600,
		color: theme.mode === "light" ? "#667781" : "#8696a0",
		padding: "16px 16px 8px",
		textTransform: "uppercase",
	},
	// Media grid
	mediaGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(4, 1fr)",
		gap: 3,
		padding: "0 8px 8px",
	},
	mediaItem: {
		position: "relative",
		paddingBottom: "100%",
		borderRadius: 4,
		overflow: "hidden",
		cursor: "pointer",
		backgroundColor: theme.mode === "light" ? "#f0f0f0" : "#1a2530",
		"&:hover": {
			opacity: 0.85,
		},
	},
	mediaImg: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
	videoBadge: {
		position: "absolute",
		bottom: 4,
		left: 4,
		display: "flex",
		alignItems: "center",
		gap: 2,
		background: "rgba(0,0,0,0.6)",
		color: "#fff",
		fontSize: 10,
		padding: "1px 5px",
		borderRadius: 3,
	},
	// Documents
	docCard: {
		margin: "0 16px 8px",
		borderRadius: 8,
		overflow: "hidden",
		background: theme.mode === "light" ? "#f9fafb" : "#1a2530",
		cursor: "pointer",
		transition: "background-color 200ms",
		"&:hover": {
			background: theme.mode === "light" ? "#f3f4f6" : "#22303c",
		},
		"&:hover button": {
			opacity: "1 !important",
		},
	},
	docBody: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 12px",
	},
	docIconBox: {
		width: 40,
		height: 40,
		flexShrink: 0,
	},
	docInfo: {
		flex: 1,
		minWidth: 0,
	},
	docName: {
		fontSize: 13,
		fontWeight: 500,
		color: theme.mode === "light" ? "#111827" : "#e9edef",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	docMeta: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		marginTop: 2,
		fontSize: 11,
		color: theme.mode === "light" ? "#6b7280" : "#8696a0",
		"& span.sep": {
			color: theme.mode === "light" ? "#d1d5db" : "#4a5568",
		},
	},
	docDownloadBtn: {
		flexShrink: 0,
		padding: 8,
		background: theme.mode === "light" ? "#fff" : "#2a3942",
		borderRadius: "50%",
		boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
		opacity: 0,
		transition: "all 200ms",
		border: "none",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		"&:hover": {
			boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
		},
	},
	// Links — reutiliza visual das bolhas do chat
	linkCard: {
		margin: "0 12px 14px",
	},
	linkHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 4,
		padding: "0 2px",
	},
	linkSenderName: {
		fontSize: 12,
		fontWeight: 600,
		color: theme.mode === "light" ? "#111b21" : "#e9edef",
	},
	linkDate: {
		fontSize: 11,
		color: theme.mode === "light" ? "#667781" : "#8696a0",
	},
	// Bolha esquerda (contato) — mesmas cores do chat
	linkBubbleLeft: {
		borderRadius: 12,
		overflow: "hidden",
		backgroundColor: theme.mode === "light" ? "#e5e5e5" : "#202c33",
		color: theme.mode === "light" ? "#303030" : "#ffffff",
		boxShadow: theme.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
		padding: 5,
	},
	// Bolha direita (você) — mesmas cores do chat
	linkBubbleRight: {
		borderRadius: 12,
		overflow: "hidden",
		backgroundColor: theme.mode === "light" ? "#d0e6ff" : "#1a3a5c",
		color: theme.mode === "light" ? "#1a1a1a" : "#ffffff",
		boxShadow: theme.mode === "light" ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
		padding: 5,
	},
	linkBubbleMedia: {
		borderRadius: 8,
		overflow: "hidden",
		marginBottom: 4,
		"& img": {
			width: "100%",
			maxHeight: 200,
			objectFit: "cover",
			display: "block",
			borderRadius: 8,
		},
		"& video": {
			width: "100%",
			maxHeight: 200,
			objectFit: "cover",
			display: "block",
			borderRadius: 8,
		},
	},
	linkBubbleText: {
		padding: "3px 6px 18px",
		fontSize: 13,
		lineHeight: 1.5,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		overflowWrap: "break-word",
		position: "relative",
		"& a": {
			color: "#1a73e8",
			textDecoration: "none",
			"&:hover": {
				textDecoration: "underline",
			},
		},
	},
	linkBubbleTime: {
		fontSize: 11,
		color: "#999",
		textAlign: "right",
		marginTop: -14,
		paddingRight: 6,
		paddingBottom: 4,
	},
	empty: {
		textAlign: "center",
		padding: "60px 16px",
		color: theme.mode === "light" ? "#999" : "#666",
		fontSize: 13,
	},
	loading: {
		textAlign: "center",
		padding: 40,
	},
}));

const MONTHS_PT = [
	"Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
	"Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

function groupByMonth(items, dateField = "createdAt") {
	const now = new Date();
	const currentMonth = now.getMonth();
	const currentYear = now.getFullYear();
	const groups = {};

	items.forEach(item => {
		const d = new Date(item[dateField]);
		const month = d.getMonth();
		const year = d.getFullYear();
		let label;
		if (month === currentMonth && year === currentYear) {
			label = "Neste mês";
		} else if (year === currentYear) {
			label = MONTHS_PT[month];
		} else {
			label = `${MONTHS_PT[month]} ${year}`;
		}
		if (!groups[label]) groups[label] = [];
		groups[label].push(item);
	});

	return groups;
}

function extractUrls(text) {
	if (!text) return [];
	const regex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;
	return text.match(regex) || [];
}

function getDomain(url) {
	try {
		return new URL(url).hostname;
	} catch {
		return url;
	}
}

function formatTime(dateStr) {
	const d = new Date(dateStr);
	return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(dateStr) {
	const d = new Date(dateStr);
	return d.toLocaleDateString("pt-BR");
}

function getFileExtension(url) {
	if (!url) return "";
	const parts = url.split(".");
	return parts.length > 1 ? parts.pop().split("?")[0].toUpperCase() : "";
}

const MediaDrawer = ({ open, onClose, ticketId, contactName }) => {
	const classes = useStyles();
	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [mediaItems, setMediaItems] = useState([]);
	const [docItems, setDocItems] = useState([]);
	const [linkItems, setLinkItems] = useState([]);
	const [docSizes, setDocSizes] = useState({});

	const loadAllMedia = useCallback(async () => {
		if (!ticketId) return;
		setLoading(true);
		try {
			let allMessages = [];
			let page = 1;
			let hasMore = true;

			while (hasMore) {
				const { data } = await api.get(`/messages/${ticketId}`, {
					params: { pageNumber: page },
				});
				const msgs = data.messages || [];
				allMessages = allMessages.concat(msgs);
				hasMore = data.hasMore;
				page++;
				if (page > 50) break;
			}

			const media = [];
			const docs = [];
			const links = [];

			allMessages.forEach(msg => {
				if (msg.mediaUrl && (msg.mediaType === "image" || msg.mediaType === "video")) {
					media.push(msg);
				} else if (msg.mediaUrl && msg.mediaType && msg.mediaType !== "image" && msg.mediaType !== "video" && msg.mediaType !== "audio") {
					docs.push(msg);
				}
				const urls = extractUrls(msg.body);
				if (urls.length > 0) {
					urls.forEach(url => {
						links.push({ ...msg, extractedUrl: url });
					});
				}
			});

			media.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
			docs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
			links.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

			setMediaItems(media);
			setDocItems(docs);
			setLinkItems(links);

			// Buscar tamanho dos documentos em paralelo
			const sizes = {};
			await Promise.all(docs.map(async (msg) => {
				try {
					// Tenta HEAD primeiro
					let res = await fetch(msg.mediaUrl, { method: "HEAD" });
					let len = res.headers.get("content-length");
					// Se HEAD não retornar tamanho, faz GET com range mínimo para forçar content-length
					if (!len) {
						res = await fetch(msg.mediaUrl, { headers: { Range: "bytes=0-0" } });
						const range = res.headers.get("content-range");
						if (range) {
							const match = range.match(/\/(\d+)$/);
							if (match) len = match[1];
						}
					}
					if (len) sizes[msg.id] = parseInt(len, 10);
				} catch {}
			}));
			setDocSizes(sizes);
		} catch (err) {
			console.error("Erro ao carregar midias:", err);
		}
		setLoading(false);
	}, [ticketId]);

	useEffect(() => {
		if (open) {
			loadAllMedia();
		}
	}, [open, loadAllMedia]);

	const renderMediaTab = () => {
		if (mediaItems.length === 0) {
			return <div className={classes.empty}>Nenhuma midia compartilhada</div>;
		}
		const groups = groupByMonth(mediaItems);
		return Object.entries(groups).map(([label, items]) => (
			<div key={label}>
				<Typography className={classes.monthLabel}>{label}</Typography>
				<div className={classes.mediaGrid}>
					{items.map(msg => (
						<div
							key={msg.id}
							className={classes.mediaItem}
							onClick={() => window.open(msg.mediaUrl, "_blank")}
						>
							{msg.mediaType === "video" ? (
								<>
									<video className={classes.mediaImg} src={msg.mediaUrl} muted />
									<div className={classes.videoBadge}>
										<span>&#9654;</span>
									</div>
								</>
							) : (
								<img className={classes.mediaImg} src={msg.mediaUrl} alt="" />
							)}
						</div>
					))}
				</div>
			</div>
		));
	};

	const cleanFileName = (body, mediaUrl) => {
		let name = body || mediaUrl?.split("/").pop() || "Documento";
		name = name.replace(/^_?\d{10,}_?\.?\d*_?/g, "");
		name = name.replace(/^\d{10,}[_-]/g, "");
		if (!name || name.length < 2) name = mediaUrl?.split("/").pop() || "Documento";
		return name;
	};

	const getExtFromUrl = (url, body) => {
		let ext = getFileExtension(url);
		if (ext && ext.length <= 5 && /^[a-zA-Z0-9]+$/.test(ext)) return ext.toLowerCase();
		if (body) {
			const parts = body.split(".");
			if (parts.length > 1) {
				const candidate = parts.pop().split(/[?#]/)[0];
				if (candidate && candidate.length <= 5 && /^[a-zA-Z]+$/.test(candidate)) return candidate.toLowerCase();
			}
		}
		return (ext || "").toLowerCase();
	};

	const formatShortDate = (dateStr) => {
		const d = new Date(dateStr);
		const day = d.getDate();
		const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
		return `${day} de ${months[d.getMonth()]}`;
	};

	const formatFileSize = (bytes) => {
		if (!bytes) return null;
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const handleDownload = async (url, fileName) => {
		try {
			const res = await fetch(url);
			const blob = await res.blob();
			const a = document.createElement("a");
			a.href = URL.createObjectURL(blob);
			a.download = fileName || "documento";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(a.href);
		} catch {
			// Fallback: abre em nova aba se falhar
			window.open(url, "_blank");
		}
	};

	const renderDocsTab = () => {
		if (docItems.length === 0) {
			return <div className={classes.empty}>Nenhum documento compartilhado</div>;
		}
		const groups = groupByMonth(docItems);
		return Object.entries(groups).map(([label, items]) => (
			<div key={label}>
				<Typography className={classes.monthLabel}>{label}</Typography>
				{items.map(msg => {
					const ext = getExtFromUrl(msg.mediaUrl, msg.body);
					const fileName = cleanFileName(msg.body, msg.mediaUrl);
					const sender = msg.fromMe ? "Você" : (contactName || "");
					const sizeStr = formatFileSize(docSizes[msg.id]);
					return (
						<div key={msg.id} className={classes.docCard} onClick={() => handleDownload(msg.mediaUrl, fileName)}>
							<div className={classes.docBody}>
								<div className={classes.docIconBox}>
									<DocFileIcon ext={ext} size={40} />
								</div>
								<div className={classes.docInfo}>
									<Typography className={classes.docName}>{fileName}</Typography>
									<div className={classes.docMeta}>
										<span>{ext ? ext.toUpperCase() : "DOC"}</span>
										{sizeStr && (
											<>
												<span className="sep">|</span>
												<span>{sizeStr}</span>
											</>
										)}
										<span className="sep">|</span>
										<span>{formatShortDate(msg.createdAt)}</span>
										{sender && (
											<>
												<span className="sep">|</span>
												<span>{sender}</span>
											</>
										)}
									</div>
								</div>
								<button
									className={classes.docDownloadBtn}
									onClick={(e) => {
										e.stopPropagation();
										handleDownload(msg.mediaUrl, fileName);
									}}
								>
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" color="currentColor" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
										<path d="M2.99969 17.0002C2.99969 17.9302 2.99969 18.3952 3.10192 18.7767C3.37932 19.8119 4.18796 20.6206 5.22324 20.898C5.60474 21.0002 6.06972 21.0002 6.99969 21.0002L16.9997 21.0002C17.9297 21.0002 18.3947 21.0002 18.7762 20.898C19.8114 20.6206 20.6201 19.8119 20.8975 18.7767C20.9997 18.3952 20.9997 17.9302 20.9997 17.0002" />
										<path d="M16.4998 11.5002C16.4998 11.5002 13.1856 16.0002 11.9997 16.0002C10.8139 16.0002 7.49976 11.5002 7.49976 11.5002M11.9997 15.0002V3.00016" />
									</svg>
								</button>
							</div>
						</div>
					);
				})}
			</div>
		));
	};

	const renderLinksTab = () => {
		if (linkItems.length === 0) {
			return <div className={classes.empty}>Nenhum link compartilhado</div>;
		}

		// Agrupar por mensagem (evitar duplicar quando msg tem múltiplos links)
		const seen = new Set();
		const uniqueMsgs = [];
		linkItems.forEach(item => {
			if (!seen.has(item.id)) {
				seen.add(item.id);
				uniqueMsgs.push(item);
			}
		});

		const groups = groupByMonth(uniqueMsgs);
		return Object.entries(groups).map(([label, items]) => (
			<div key={label}>
				<Typography className={classes.monthLabel}>{label}</Typography>
				{items.map(item => {
					const senderName = item.fromMe ? "Você" : (contactName || "Contato");
					const otherName = item.fromMe ? (contactName || "") : "Você";
					const senderLabel = otherName ? `${senderName} \u2022 ${otherName}` : senderName;
					const dateStr = format(parseISO(item.createdAt), "dd/MM/yyyy");
					const timeStr = format(parseISO(item.createdAt), "HH:mm");
					const bubbleClass = item.fromMe ? classes.linkBubbleRight : classes.linkBubbleLeft;

					return (
						<div key={item.id} className={classes.linkCard}>
							<div className={classes.linkHeader}>
								<Typography className={classes.linkSenderName}>{senderLabel}</Typography>
								<Typography className={classes.linkDate}>{dateStr}</Typography>
							</div>
							<div className={bubbleClass}>
								{item.mediaUrl && item.mediaType === "image" && (
									<div className={classes.linkBubbleMedia}>
										<ModalImageCors imageUrl={item.mediaUrl} />
									</div>
								)}
								{item.mediaUrl && item.mediaType === "video" && (
									<div className={classes.linkBubbleMedia}>
										<video src={item.mediaUrl} controls style={{ width: "100%", maxHeight: 200, borderRadius: 8 }} />
									</div>
								)}
								<div className={classes.linkBubbleText}>
									<MarkdownWrapper>{item.body}</MarkdownWrapper>
								</div>
								<div className={classes.linkBubbleTime}>{timeStr}</div>
							</div>
						</div>
					);
				})}
			</div>
		));
	};

	return (
		<Drawer
			className={classes.drawer}
			anchor="right"
			open={open}
			onClose={onClose}
		>
			<div className={classes.header}>
				<Typography className={classes.headerTitle}>
					Midia, documentos e links
				</Typography>
				<IconButton
					size="small"
					onClick={onClose}
					className={classes.closeBtn}
				>
					<CloseXIcon style={{ fontSize: 18 }} />
				</IconButton>
			</div>

			<Tabs
				value={activeTab}
				onChange={(e, v) => setActiveTab(v)}
				className={classes.tabs}
				indicatorColor="primary"
				textColor="primary"
				variant="fullWidth"
			>
				<Tab label="Midia" />
				<Tab label="Documentos" />
				<Tab label="Links" />
			</Tabs>

			<div className={classes.content}>
				{loading ? (
					<div className={classes.loading}>
						<CircularProgress size={24} />
					</div>
				) : (
					<>
						{activeTab === 0 && renderMediaTab()}
						{activeTab === 1 && renderDocsTab()}
						{activeTab === 2 && renderLinksTab()}
					</>
				)}
			</div>
		</Drawer>
	);
};

export default MediaDrawer;
